import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  downloadPortalCouponSheet,
  downloadPortalInterestLetter,
  downloadPortalStatement,
  getPortalDocumentsCatalog,
} from '../../../endpoints/portalLoans';
import { getApiErrorMessage, useApiGet } from '../../../utils/httpClientTs';
import { formatPortalCurrency } from '../mock/store';
import { ELF_TEAL, elfOutlinedButtonSx, elfPrimaryButtonSx } from '../elfTheme';

const LoanDocuments: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useApiGet(
    ['portal-loans-documents'],
    getPortalDocumentsCatalog,
    { retry: 1 }
  );

  const [startMonth, setStartMonth] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [autopayPromptOpen, setAutopayPromptOpen] = useState(false);

  useEffect(() => {
    if (!data) return;
    setStartMonth(data.coupons.default_start_month);
    setEndMonth(data.coupons.default_end_month);
    if (data.coupons.default_amount != null) {
      setAmount(data.coupons.default_amount.toFixed(2));
    }
  }, [data]);

  const parsedAmount = Number.parseFloat(amount);
  const minAmount = data?.coupons.min_amount ?? 0;
  const amountOk =
    Number.isFinite(parsedAmount) &&
    parsedAmount > 0 &&
    (minAmount <= 0 || parsedAmount + 0.009 >= minAmount);

  const runCouponDownload = async (): Promise<boolean> => {
    if (!amountOk) return false;
    setBusy('coupons');
    setActionError(null);
    try {
      await downloadPortalCouponSheet({
        amount: Math.round(parsedAmount * 100) / 100,
        start_month: startMonth,
        end_month: endMonth,
      });
      return true;
    } catch (err) {
      setActionError(getApiErrorMessage(err) || 'Unable to download coupons.');
      return false;
    } finally {
      setBusy(null);
    }
  };

  const handleCouponClick = () => {
    if (!amountOk || !data) return;
    const autopay = data.coupons.active_autopay_amount;
    if (
      autopay != null &&
      Number.isFinite(autopay) &&
      Math.abs(autopay - parsedAmount) > 0.009
    ) {
      setAutopayPromptOpen(true);
      return;
    }
    void runCouponDownload();
  };

  const handleInterestDownload = async () => {
    if (!data?.interest_letter.available) return;
    setBusy('interest');
    setActionError(null);
    try {
      await downloadPortalInterestLetter(data.interest_letter.tax_year);
    } catch (err) {
      setActionError(
        getApiErrorMessage(err) || 'Unable to download interest letter.'
      );
    } finally {
      setBusy(null);
    }
  };

  const handleStatementDownload = async (yearMonth: string) => {
    setBusy(`stmt-${yearMonth}`);
    setActionError(null);
    try {
      await downloadPortalStatement(yearMonth);
    } catch (err) {
      setActionError(
        getApiErrorMessage(err) || 'Unable to download statement.'
      );
    } finally {
      setBusy(null);
    }
  };

  if (isLoading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress sx={{ color: ELF_TEAL }} />
      </Box>
    );
  }

  if (isError && !data) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => refetch()}>
            Retry
          </Button>
        }
      >
        {getApiErrorMessage(error) || 'Unable to load documents.'}
      </Alert>
    );
  }

  if (!data) {
    return <Alert severity="info">No documents available.</Alert>;
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Documents
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Download payment coupons, your interest-paid letter, and monthly
        statements. Form 1098-E is not available here.
      </Typography>

      {actionError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setActionError(null)}
        >
          {actionError}
        </Alert>
      )}

      <Stack spacing={3}>
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="h6" gutterBottom>
            Payment coupons
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Printable slips to mail with a check. Each coupon shows the
            International Office address, your account (
            {data.coupons.account_label || '—'}), and the amount. Up to{' '}
            {data.coupons.max_coupons} months.
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <TextField
              label="Start month"
              type="month"
              size="small"
              value={startMonth}
              onChange={(e) => setStartMonth(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={!!busy}
            />
            <TextField
              label="End month"
              type="month"
              size="small"
              value={endMonth}
              onChange={(e) => setEndMonth(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={!!busy}
            />
            <TextField
              label="Amount"
              type="number"
              size="small"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{
                min: minAmount > 0 ? minAmount : 0.01,
                step: '0.01',
              }}
              helperText={
                minAmount > 0
                  ? `Minimum ${formatPortalCurrency(minAmount)}`
                  : undefined
              }
              disabled={!!busy}
              sx={{ maxWidth: 180 }}
            />
          </Stack>
          <Button
            variant="contained"
            onClick={handleCouponClick}
            disabled={!amountOk || !!busy || !startMonth || !endMonth}
            sx={elfPrimaryButtonSx}
          >
            {busy === 'coupons' ? 'Generating…' : 'Download coupon sheet'}
          </Button>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="h6" gutterBottom>
            Interest paid letter
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Summary of interest applied from payments in tax year{' '}
            {data.interest_letter.tax_year}. This is not Form 1098-E.
          </Typography>
          {data.interest_letter.available ? (
            <Button
              variant="outlined"
              onClick={() => void handleInterestDownload()}
              disabled={!!busy}
              sx={elfOutlinedButtonSx}
            >
              {busy === 'interest'
                ? 'Generating…'
                : `Download ${data.interest_letter.tax_year} letter`}
            </Button>
          ) : (
            <Alert severity="info">
              Available after January 31 for the prior tax year.
            </Alert>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="h6" gutterBottom>
            Monthly statements
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Past months only (last 12).
          </Typography>
          <Stack spacing={1.5}>
            {data.statements.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No statement months available yet.
              </Typography>
            ) : (
              data.statements.map((stmt) => (
                <Stack
                  key={stmt.year_month}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                >
                  <Typography>{stmt.label}</Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      void handleStatementDownload(stmt.year_month)
                    }
                    disabled={!!busy}
                    sx={elfOutlinedButtonSx}
                  >
                    {busy === `stmt-${stmt.year_month}`
                      ? 'Generating…'
                      : 'Download'}
                  </Button>
                </Stack>
              ))
            )}
          </Stack>
        </Paper>
      </Stack>

      <Dialog
        open={autopayPromptOpen}
        onClose={() => {
          if (busy !== 'coupons') setAutopayPromptOpen(false);
        }}
      >
        <DialogTitle>Coupon amount differs from autopay</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your coupon amount does not match your current autopay amount
            {data.coupons.active_autopay_amount != null
              ? ` (${formatPortalCurrency(data.coupons.active_autopay_amount)})`
              : ''}
            . Coupons are for mail-in checks only and do not change your loan
            installment. Do you want to update autopay after downloading?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAutopayPromptOpen(false);
              void runCouponDownload();
            }}
            disabled={busy === 'coupons'}
          >
            Download only
          </Button>
          <Button
            variant="contained"
            sx={elfPrimaryButtonSx}
            onClick={() => {
              setAutopayPromptOpen(false);
              void (async () => {
                const ok = await runCouponDownload();
                if (ok) navigate('/portal/loans/autopay');
              })();
            }}
            disabled={busy === 'coupons'}
          >
            Download and update autopay
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoanDocuments;
